import { DragEvent, useEffect, useId, useRef, useState } from "react";
import UploadFileRoundedIcon from "@mui/icons-material/UploadFileRounded";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import { Button } from "../Button";
import { IconButton } from "../IconButton";
import { VISUALLY_HIDDEN } from "../visuallyHidden";
import s from "./FileDrop.module.scss";

type Props = {
  /** The zone's big line ("Drop your file here, or choose one"); also the drop area's name. */
  label: string;
  hint?: string;
  /** Same syntax as `<input accept>`: ".xlsx,.csv", "text/csv", "image/*". Also applied to dropped files. */
  accept: string;
  chooseLabel: string;
  /** Called with one file that passed every check (type, not empty, size). */
  onFile: (f: File) => void;
  /** The chosen file: shown as a row instead of the zone, and announced politely. */
  file?: { name: string; meta: string };
  replaceLabel: string;
  /** Shown (as an alert) when a file does not match `accept`. */
  wrongTypeError: string;
  /** Shown when several files are dropped at once ("Only one file can be imported at a time."); none is taken. */
  multipleError: string;
  /** Shown when the file has nothing in it (0 bytes). */
  emptyError: string;
  onRemove?: () => void;
  removeLabel?: string;
  disabled?: boolean;
  /** A message from the page (e.g. the file could not be read). */
  error?: string;
} & ({ maxBytes?: undefined; tooLargeError?: undefined } | { maxBytes: number; tooLargeError: string });

/** Does the file match an `accept` list? Extensions case-insensitively, MIME types with `type/*` wildcards. */
export const fileMatchesAccept = (f: File, accept: string) => {
  const rules = accept.split(",").map((r) => r.trim().toLowerCase()).filter(Boolean);
  if (!rules.length) return true;
  const name = f.name.toLowerCase();
  const type = (f.type || "").toLowerCase();
  return rules.some((r) => (r.startsWith(".") ? name.endsWith(r)
    : r.endsWith("/*") ? type.startsWith(r.slice(0, -1)) : type === r));
};

const carriesFiles = (e: DragEvent) => Array.from(e.dataTransfer?.types ?? []).includes("Files");

/**
 * A drop zone with a real `<input type=file>` behind a kit Button: Enter/Space open the picker; the input itself is
 * `hidden` (neither seen nor a tab stop; the button clicks it, as react-dropzone does). Dropped and picked files go
 * through the same checks (one file, `accept`, not empty, `maxBytes`); a refused one shows its message as an alert and
 * is not passed on. Once a file is chosen the zone becomes the file's row ("Choose another", optional Remove), which
 * still takes a drop; a polite status line (always in the page) says which file is chosen. When a file is taken or
 * removed and the button the user was on goes away, focus moves to the new button instead of being lost.
 */
export const FileDrop = (props: Props) => {
  const { label, hint, accept, chooseLabel, onFile, file, replaceLabel, wrongTypeError, multipleError, emptyError, onRemove, removeLabel, disabled, error,
    maxBytes, tooLargeError } = props;
  const id = useId();
  const input = useRef<HTMLInputElement>(null);
  const choose = useRef<HTMLButtonElement>(null);
  const replace = useRef<HTMLButtonElement>(null);
  /** Set only when this component handed a file over (or removed one): the next swap of states may move focus. */
  const acted = useRef(false);
  const [over, setOver] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);

  const take = (files: FileList | File[] | null | undefined) => {
    if (disabled || !files || !files.length) return;
    if (files.length > 1) { setRefusal(multipleError); return; }
    const f = files[0];
    const why = !fileMatchesAccept(f, accept) ? wrongTypeError
      : f.size === 0 ? emptyError
      : maxBytes !== undefined && f.size > maxBytes ? tooLargeError ?? null : null;
    if (why) { setRefusal(why); return; }
    setRefusal(null);
    acted.current = true;
    onFile(f);
  };

  // The picker closed without a file: nothing was handed over, so nothing may move focus later.
  useEffect(() => {
    const el = input.current;
    const clear = () => { acted.current = false; };
    el?.addEventListener("cancel", clear);
    return () => el?.removeEventListener("cancel", clear);
  }, []);

  const hasFile = !!file;
  useEffect(() => {
    if (!acted.current) return;
    acted.current = false;
    const active = document.activeElement;
    if (active && active !== document.body && active !== input.current) return; // focus is somewhere on purpose
    (hasFile ? replace.current : choose.current)?.focus();
  }, [hasFile, file?.name]);

  const onDrag = (e: DragEvent, entering: boolean) => {
    if (!carriesFiles(e)) return; // dragged text or a link: not ours
    e.preventDefault();
    if (disabled) return;
    if (entering) setOver(true);
    else if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOver(false);
  };
  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setOver(false);
    take(e.dataTransfer?.files);
  };

  const message = refusal ?? error;
  const labelId = `${id}-label`;
  const hintId = hint ? `${id}-hint` : undefined;
  const errId = message ? `${id}-err` : undefined;
  const nameId = `${id}-name`;
  const metaId = `${id}-meta`;
  const pick = () => input.current?.click();

  return (
    <div className={s.wrap}>
      <div role="group" aria-labelledby={hasFile ? nameId : labelId}
        className={[hasFile ? s.fileRow : s.zone, over ? s.over : "", disabled ? s.disabled : ""].filter(Boolean).join(" ")}
        onDragEnter={(e) => onDrag(e, true)} onDragOver={(e) => onDrag(e, true)} onDragLeave={(e) => onDrag(e, false)} onDrop={onDrop}>
        {file ? (
          <>
            <span aria-hidden className={s.fileIcon}><DescriptionRoundedIcon /></span>
            <span className={s.fileText}>
              <bdi id={nameId} className={s.fileName}>{file.name}</bdi>
              <span id={metaId} className={s.fileMeta}>{file.meta}</span>
            </span>
            <span className={s.fileActions}>
              <Button ref={replace} variant="ghost" onClick={pick} disabled={disabled}
                aria-describedby={[nameId, metaId, errId].filter(Boolean).join(" ")}>{replaceLabel}</Button>
              {onRemove && removeLabel && (
                <IconButton label={removeLabel} icon={<CloseRoundedIcon />} disabled={disabled}
                  onClick={() => { acted.current = true; setRefusal(null); onRemove(); }} />
              )}
            </span>
          </>
        ) : (
          <>
            <span aria-hidden className={s.zoneIcon}><UploadFileRoundedIcon /></span>
            <span id={labelId} className={s.label}>{label}</span>
            {hint && <span id={hintId} className={s.hint}>{hint}</span>}
            <Button ref={choose} onClick={pick} disabled={disabled} className={s.choose}
              aria-describedby={[labelId, hintId, errId].filter(Boolean).join(" ")}>{chooseLabel}</Button>
          </>
        )}
        <input ref={input} type="file" accept={accept} hidden disabled={disabled}
          onChange={(e) => { const files = Array.from(e.target.files ?? []); e.target.value = ""; take(files); }} />
      </div>
      <span role="status" className={VISUALLY_HIDDEN}>{file ? <><bdi>{file.name}</bdi> · {file.meta}</> : null}</span>
      {message && <p id={errId} role="alert" className={s.error}><ErrorOutlineRoundedIcon aria-hidden className={s.errIcon} />{message}</p>}
    </div>
  );
};
